import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { updateDraft, updateDraftPatient, updateCase } from '../../../services/MedNecService';
import type { MedNecCaseView, PatientView } from '../../../services/MedNecService/types/medNecTypes';
import { createEmptyCase } from '../../../services/MedNecService/types/medNecTypes';

export type FieldMode = 'create' | 'edit' | 'view';

const emptyCase = createEmptyCase();

/** Selects the appropriate case data based on mode. */
export function useCaseFieldData(mode: FieldMode): MedNecCaseView {
  const draft = useAppSelector(state => state.medNecCase.draftCase);
  const current = useAppSelector(state => state.medNecCase.currentCase);

  return (mode === 'create' ? draft : current) ?? emptyCase;
}

/** Returns a dispatcher that writes to the correct slice based on mode. */
export function useCaseFieldDispatch(mode: FieldMode) {
  const dispatch = useAppDispatch();
  const currentPatient = useAppSelector(state => state.medNecCase.currentCase?.patient);
  const readOnly = mode === 'view';

  const dispatchUpdate = useCallback((fields: Partial<MedNecCaseView>) => {
    if (readOnly) return;
    if (mode === 'create') {
      dispatch(updateDraft(fields));
    } else {
      dispatch(updateCase(fields));
    }
  }, [dispatch, mode, readOnly]);

  const dispatchPatientUpdate = useCallback((fields: Partial<PatientView>) => {
    if (readOnly) return;
    if (mode === 'create') {
      dispatch(updateDraftPatient(fields));
    } else {
      const merged = { ...(currentPatient ?? { firstName: '', lastName: '', dateOfBirth: '', memberId: '' }), ...fields };
      dispatch(updateCase({ patient: merged }));
    }
  }, [dispatch, mode, readOnly, currentPatient]);

  return { dispatchUpdate, dispatchPatientUpdate, readOnly };
}
